import React, { useState, useEffect } from 'react';

const Clock = ({ theme: parentTheme }) => {
  // Load saved settings from localStorage
  const loadClockSettings = () => {
    try {
      const savedSettings = localStorage.getItem('webos-clock-settings');
      if (savedSettings) {
        return JSON.parse(savedSettings);
      }
    } catch (e) {
      console.error('Failed to load clock settings:', e);
    }
    return {
      format24Hour: false,
      showSeconds: true,
      showDate: true, 
      dateFormat: 'full', // 'full', 'short', 'numeric'
      theme: 'default' // 'default', 'minimal', 'digital', 'analog'
    };
  };

  const initialSettings = loadClockSettings();
  
  const [time, setTime] = useState(new Date());
  const [settings, setSettings] = useState(initialSettings);
  const [showSettings, setShowSettings] = useState(false);
  
  // Use the parent theme (dark/light) as our base theme context
  const isDarkMode = parentTheme === 'dark';
  
  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setTime(new Date());
    }, 1000);
    
    return () => clearInterval(timer);
  }, []);
  
  // Save settings when they change
  useEffect(() => {
    localStorage.setItem('webos-clock-settings', JSON.stringify(settings));
  }, [settings]);
  
  // Format time based on settings
  const formatTime = () => {
    const hours = settings.format24Hour 
      ? time.getHours() 
      : (time.getHours() % 12 || 12);
      
    const minutes = time.getMinutes().toString().padStart(2, '0');
    const seconds = time.getSeconds().toString().padStart(2, '0');
    const ampm = time.getHours() >= 12 ? 'PM' : 'AM';
    
    return `${hours}:${minutes}${settings.showSeconds ? `:${seconds}` : ''}${!settings.format24Hour ? ` ${ampm}` : ''}`;
  };
  
  // Format date based on settings
  const formatDate = () => {
    if (!settings.showDate) return '';
    
    const options = { 
      weekday: settings.dateFormat === 'full' ? 'long' : 'short',
      year: settings.dateFormat === 'numeric' ? 'numeric' : undefined,
      month: settings.dateFormat === 'full' ? 'long' : 'short',
      day: 'numeric'
    };
    
    return time.toLocaleDateString(undefined, options);
  };
  
  // Toggle settings
  const toggleSettings = () => {
    setShowSettings(!showSettings);
  };
  
  // Update a specific setting
  const updateSetting = (key, value) => {
    setSettings({
      ...settings,
      [key]: value
    });
  };
  
  // Get theme-based styles
  const getThemeStyles = () => {
    const baseStyles = {
      container: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        padding: '15px',
        borderRadius: '10px',
        color: isDarkMode ? '#fff' : '#333',
        backdropFilter: 'blur(15px)',
        fontFamily: 'Arial, sans-serif'
      },
      time: {
        fontSize: '42px',
        fontWeight: '500',
        margin: '10px 0'
      },
      date: {
        fontSize: '16px',
        margin: '5px 0'
      },
      settingsButton: {
        position: 'absolute',
        top: '10px',
        right: '10px',
        background: isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)',
        border: 'none',
        borderRadius: '5px',
        padding: '5px 10px',
        cursor: 'pointer',
        color: isDarkMode ? '#fff' : '#333'
      },
      settingsPanel: {
        marginTop: '20px',
        background: isDarkMode ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0.05)',
        padding: '15px',
        borderRadius: '8px'
      },
      settingRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        margin: '10px 0'
      }
    };
    
    // Theme-specific overrides that take into account the parent theme as well
    switch (settings.theme) {
      case 'minimal':
        return {
          ...baseStyles,
          container: {
            ...baseStyles.container,
            background: isDarkMode ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.1)',
            justifyContent: 'center',
            alignItems: 'center'
          },
          time: {
            ...baseStyles.time,
            fontSize: '36px',
            fontWeight: '300'
          }
        };
      case 'digital':
        return {
          ...baseStyles,
          container: {
            ...baseStyles.container,
            background: isDarkMode ? 'rgba(0, 0, 20, 0.6)' : 'rgba(240, 240, 255, 0.6)',
            border: isDarkMode ? '1px solid rgba(0, 200, 255, 0.3)' : '1px solid rgba(0, 120, 255, 0.2)',
            justifyContent: 'center',
            alignItems: 'center'
          },
          time: {
            ...baseStyles.time,
            fontFamily: 'monospace',
            color: isDarkMode ? '#0cf' : '#07f',
            textShadow: isDarkMode ? '0 0 10px rgba(0, 200, 255, 0.8)' : '0 0 10px rgba(0, 120, 255, 0.3)'
          },
          date: {
            ...baseStyles.date,
            color: isDarkMode ? '#0af' : '#38f'
          }
        };
      default:
        return {
          ...baseStyles,
          container: {
            ...baseStyles.container,
            background: isDarkMode 
              ? 'linear-gradient(135deg, rgba(50, 60, 100, 0.5), rgba(30, 30, 60, 0.5))' 
              : 'linear-gradient(135deg, rgba(255, 255, 255, 0.7), rgba(240, 245, 255, 0.7))',
            justifyContent: 'center',
            alignItems: 'center'
          }
        };
    }
  };
  
  const styles = getThemeStyles();
  
  return (
    <div style={styles.container}>
      <div style={styles.time}>{formatTime()}</div>
      {settings.showDate && <div style={styles.date}>{formatDate()}</div>}
      
      <button 
        style={styles.settingsButton}
        onClick={toggleSettings}
      >
        ⚙️
      </button>
      
      {showSettings && (
        <div style={styles.settingsPanel}>
          <div style={styles.settingRow}>
            <label style={{ color: isDarkMode ? 'white' : '#333' }}>24-Hour Format</label>
            <input 
              type="checkbox" 
              checked={settings.format24Hour}
              onChange={(e) => updateSetting('format24Hour', e.target.checked)}
            />
          </div>
          
          <div style={styles.settingRow}>
            <label style={{ color: isDarkMode ? 'white' : '#333' }}>Show Seconds</label>
            <input 
              type="checkbox" 
              checked={settings.showSeconds}
              onChange={(e) => updateSetting('showSeconds', e.target.checked)}
            />
          </div>
          
          <div style={styles.settingRow}>
            <label style={{ color: isDarkMode ? 'white' : '#333' }}>Show Date</label>
            <input 
              type="checkbox" 
              checked={settings.showDate}
              onChange={(e) => updateSetting('showDate', e.target.checked)}
            />
          </div>
          
          <div style={styles.settingRow}>
            <label style={{ color: isDarkMode ? 'white' : '#333' }}>Date Format</label>
            <select 
              value={settings.dateFormat}
              onChange={(e) => updateSetting('dateFormat', e.target.value)}
              style={{ 
                background: isDarkMode ? 'rgba(0, 0, 0, 0.3)' : 'rgba(255, 255, 255, 0.7)', 
                color: isDarkMode ? '#fff' : '#333',
                border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)'}`, 
                padding: '5px' 
              }}
            >
              <option value="full">Full</option>
              <option value="short">Short</option>
              <option value="numeric">Numeric</option>
            </select>
          </div>
          
          <div style={styles.settingRow}>
            <label style={{ color: isDarkMode ? 'white' : '#333' }}>Theme</label>
            <select 
              value={settings.theme}
              onChange={(e) => updateSetting('theme', e.target.value)}
              style={{ 
                background: isDarkMode ? 'rgba(0, 0, 0, 0.3)' : 'rgba(255, 255, 255, 0.7)', 
                color: isDarkMode ? '#fff' : '#333',
                border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)'}`, 
                padding: '5px' 
              }}
            >
              <option value="default">Default</option>
              <option value="minimal">Minimal</option>
              <option value="digital">Digital</option>
            </select>
          </div>
        </div>
      )}
    </div>
  );
};

export default Clock; 